import { ui } from '@clientio/jointjs'
import { NodeType } from '../../../core/workflow'
import { HaloHandlesEnum } from './index'
import { colorCode } from '../nodes'
import { getCompatibleNodes } from '../../../utils/available-nodes'
import { store } from 'src/store/state'

/**
 * @typedef {Object} HaloHandle
 * @extends joint.ui.HandleOptions
 * @property {{handleName: HaloHandlesEnum, sourceNode: CanvasWorkflowNode, node?: PaletteNode}} data
 * */

/**
 * @callback HaloCallback
 * @param {CanvasWorkflowNode} sourceNode
 * @param {PaletteNode} [node]
 * @returns {HaloHandle} - handle attributes
 * */

// WhatsApp SVG Icon
const WhatsAppSVGIcon = `<svg class="MuiSvgIcon-root MuiSvgIcon-root svg-icon" focusable="false" aria-hidden="true" viewBox="0 0 24 24" tabindex="-1" title="WhatsApp" style="fill: #fff;width: 24px;height: 24px;"><path d="M16.75 13.96c.25.13.41.2.46.3.06.11.04.61-.21 1.18-.2.56-1.24 1.1-1.7 1.12-.46.02-.47.36-2.96-.73-2.49-1.09-3.99-3.75-4.11-3.92-.12-.17-.96-1.38-.92-2.61.05-1.22.69-1.8.95-2.04.24-.26.51-.29.68-.26h.47c.15 0 .36-.06.55.45l.69 1.87c.06.13.1.28.01.44l-.27.41-.39.42c-.12.12-.26.25-.12.5.12.26.62 1.09 1.32 1.78.91.88 1.71 1.17 1.95 1.3.24.14.39.12.54-.04l.81-.94c.19-.25.35-.19.58-.11l1.67.88M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10c-1.97 0-3.8-.57-5.35-1.55L2 22l1.55-4.65A9.969 9.969 0 0 1 2 12 10 10 0 0 1 12 2m0 2a8 8 0 0 0-8 8c0 1.72.54 3.31 1.46 4.61L4.5 19.5l2.89-.96A7.95 7.95 0 0 0 12 20a8 8 0 0 0 8-8 8 8 0 0 0-8-8z"></path></svg>`

/**
 * Renders the fork handle for a given node.
 * @param {*} node: The node to render the fork handle for.
 * @returns {string} The HTML string for the fork handle.
 */
const renderNodeForkHandle = node => {
  const isImageIcon = node.type !== NodeType.WHATSAPP && node.node_icon_link.includes('/images/jb/')
  const iconHtml = isImageIcon
    ? `<img src=${node.node_icon_link} alt=${node.name} class="joint-halo-icon" loading="lazy" style="width: 20px; height: 20px;" />`
    : node.type === NodeType.WHATSAPP
      ? WhatsAppSVGIcon
      : `<span class="material-symbols-outlined joint-halo-icon">${node.node_icon_link}</span>`

  return `<div class="halo-item">
        <div class="halo-item-icon-wrapper" style="background: ${colorCode[node.type]}">
          ${iconHtml}
        </div>
        <span class="halo-item-text">${node.name}</span>
      </div>`
}

/**
 * Attributes for the halo handles.
 * @type {{[key:HaloHandlesEnum]: HaloCallback}}
 */
export const haloHandleAttributes = {
  /** @type {HaloCallback} */
  link: sourceNode => ({
    ...ui.Halo.getDefaultHandle('link'),
    name: HaloHandlesEnum.LINK,
    content: `
    <div class="halo-item">
      <span class="halo-item-text link-text">Link</span>
    </div>`,
    position: 'halo-group',
    // attrs: { '.handle': { 'data-tooltip': 'link' } },
    data: { handleName: HaloHandlesEnum.LINK, sourceNode },
    hideOnDrag: true
  }),
  /** @type {HaloCallback} */
  removeLinks: sourceNode => ({
    name: HaloHandlesEnum.REMOVE_LINKS,
    content: `
    <div class="halo-item">
      <span class="material-symbols-outlined joint-halo-icon">content_cut</span>
      <span class="halo-item-text">Remove All Links</span>
    </div>`,
    position: 'halo-group',
    // attrs: { '.handle': { 'data-tooltip': 'Removes all links connected to this node' } },
    data: { handleName: HaloHandlesEnum.REMOVE_LINKS, sourceNode }
  }),
  /** @type {HaloCallback} */
  cloneNode: sourceNode => ({
    name: HaloHandlesEnum.CLONE,
    content: `<div class="halo-item">
    <span class="material-symbols-outlined joint-halo-icon">content_copy</span>
    <span class="halo-item-text">Clone</span>
  </div>`,
    position: 'halo-group',
    // attrs: { '.handle': { 'data-tooltip': 'Clone' } },
    data: { handleName: HaloHandlesEnum.CLONE, sourceNode }
  }),
  // add divider long line here
  divider: () => ({
    name: 'divider',
    content: `<div class="halo-divider"></div>`,
    position: 'halo-group'
  }),
  sectionHeader: (key, label) => ({
    name: `section-header-${key}`,
    content: `<span class="halo-section-header-text">${label}</span>`,
    position: 'halo-group',
    attrs: {
      '.handle': {
        style: 'pointer-events: none; cursor: default;'
      }
    }
  }),

  /** @type HaloCallback */
  fork: (sourceNode, node) => ({
    name: `fork-${node.type} fork-item`,
    content: renderNodeForkHandle(node),
    position: 'halo-group',
    // attrs: { '.handle': { 'data-tooltip': `${node.description}` } },
    data: { handleName: HaloHandlesEnum.FORK, node, sourceNode }
  }),
  /** @type HaloCallback */
  search: (sourceNode, searchTerm = '') => ({
    name: HaloHandlesEnum.SEARCH,
    content: `<div class="halo-search-container" style="pointer-events: auto;">
      <span class="material-symbols-outlined halo-search-icon">search</span>
      <input
        type="text"
        class="halo-search-input"
        id="halo-search-${sourceNode.id}"
        placeholder="Search nodes..."
        autocomplete="off"
        value="${searchTerm}"
        style="pointer-events: auto;"
      />
    </div>`,
    position: 'halo-group',
    attrs: {
      '.handle': {
        style: 'pointer-events: none; position: sticky; top: 0; z-index: 10; margin-bottom: 0;'
      }
    },
    data: { handleName: HaloHandlesEnum.SEARCH, sourceNode }
  })
}

/**
 * Get all palette nodes from the state.
 * @returns {Array} The array of palette nodes.
 */
const getPaletteNodesFromState = () => store.getState().ui.palette.nodes

/**
 * Resolves compatible nodes for fork handles, using the provided filtered list or falling back to all compatible nodes.
 * @param {CanvasWorkflowNode} nodeData
 * @param {Array|null} filteredNodes
 * @returns {Array}
 */
const resolveCompatibleNodes = (nodeData, filteredNodes) => {
  if (filteredNodes && filteredNodes.length > 0) {
    return filteredNodes
  }
  try {
    return getCompatibleNodes(nodeData, getPaletteNodesFromState())
  } catch (error) {
    console.warn('Error getting compatible nodes, using empty array:', error)
    return []
  }
}

/**
 * Gets the halo handles for a given node based on its type.
 * @param {DiagramNode} node - The node object.
 * @param {Array} [filteredNodes] - Optional filtered nodes from search, if not provided will use all compatible nodes.
 * @param {boolean} [isTempNode] - Whether this is a temporary node.
 * @returns {HaloHandle[]} An array of halo handle attributes.
 */
/**
 * Helper to determine the item_type of a node item if it is not provided.
 * @param {Object} nodeItem
 * @returns {string}
 */
const getItemType = nodeItem => {
  if (nodeItem.item_type) {
    return nodeItem.item_type
  }
  const type = nodeItem.type
  if (type === 'start' || type === 'end') {
    return 'start/end_nodes'
  }
  if (['email', 'sms', 'whatsapp', 'rcs'].includes(type)) {
    return 'channel_nodes'
  }
  if (['case', 'if-else'].includes(type)) {
    return 'decision_nodes'
  }
  if (['wait_until', 'wait'].includes(type)) {
    return 'wait_nodes'
  }
  return 'channel_nodes'
}

/**
 * Gets the halo handles for a given node based on its type.
 * @param {DiagramNode} node - The node object.
 * @param {Array} [filteredNodes] - Optional filtered nodes from search, if not provided will use all compatible nodes.
 * @param {boolean} [isTempNode] - Whether this is a temporary node.
 * @returns {HaloHandle[]} An array of halo handle attributes.
 */
export const getNodeHaloHandles = (node, filteredNodes = null, isTempNode = false, searchTerm = '') => {
  const nodeData = node.getCachedOpt()

  if (nodeData.type === 'stop' || nodeData.type === 'end') {
    return []
  }

  const compatibleNodes = resolveCompatibleNodes(nodeData, filteredNodes)

  const handles = [haloHandleAttributes.search(nodeData, searchTerm)]

  // Group fork handles by item_type
  const groups = {
    'start/end_nodes': { label: 'Start/End Nodes', items: [] },
    channel_nodes: { label: 'Channel Nodes', items: [] },
    decision_nodes: { label: 'Decision Nodes', items: [] },
    wait_nodes: { label: 'Wait Nodes', items: [] }
  }

  compatibleNodes.forEach(nodeItem => {
    const itemType = getItemType(nodeItem)
    const forkHandle = haloHandleAttributes.fork(nodeData, nodeItem)
    if (groups[itemType]) {
      groups[itemType].items.push(forkHandle)
    } else {
      groups['channel_nodes'].items.push(forkHandle)
    }
  })

  if (isTempNode) {
    Object.keys(groups).forEach(key => {
      const group = groups[key]
      if (group.items.length > 0) {
        handles.push(haloHandleAttributes.sectionHeader(key, group.label))
        handles.push(...group.items)
      }
    })
    return handles
  }

  const actionItems = []
  actionItems.push(haloHandleAttributes.link(nodeData))
  actionItems.push(haloHandleAttributes.removeLinks(nodeData))

  // Don't allow cloning of start nodes
  if (nodeData.type !== 'start') {
    actionItems.push(haloHandleAttributes.cloneNode(nodeData))
  }

  if (actionItems.length > 0) {
    handles.push(haloHandleAttributes.sectionHeader('actions', 'Actions'))
    handles.push(...actionItems)
  }

  handles.push(haloHandleAttributes.divider())

  Object.keys(groups).forEach(key => {
    const group = groups[key]
    if (group.items.length > 0) {
      handles.push(haloHandleAttributes.sectionHeader(key, group.label))
      handles.push(...group.items)
    }
  })

  return handles
}
